import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth } from "./PortalAuthContext";
import { formatApiError } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import UsePwaManifest from "@/hooks/usePwaManifest";

export default function PortalLogin() {
  const { employee, login, loading } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && employee) {
    const from = location.state?.from || "/dolgozo";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success(t("auth.welcome"));
      navigate("/dolgozo", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("auth.invalid_credentials"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="portal-login"
      className="relative grid min-h-screen place-items-center bg-zinc-50 px-4 py-8 antialiased dark:bg-zinc-950"
    >
      <UsePwaManifest href="/manifest-portal.json" title="SIPX Dolgozó" />
      {/* Soft background gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60 dark:opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 20%, rgba(59,130,246,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="relative w-full max-w-[400px]">
        {/* Brand */}
        <div className="mb-7 flex items-center justify-center gap-2.5">
          <BrandMark size={40} rounded="2xl" className="shadow-[0_4px_12px_-2px_rgba(0,0,0,0.18)]" />
          <div className="text-left leading-tight">
            <p className="text-[15px] font-semibold tracking-tight">SIPX Group</p>
            <p className="text-[12px] text-zinc-500">{t("nav.dashboard")}</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-3xl bg-white p-7 shadow-[0_1px_3px_0_rgba(0,0,0,0.06),0_8px_28px_-8px_rgba(0,0,0,0.08)] dark:bg-zinc-900 md:p-8">
          <h1 className="text-[26px] font-semibold tracking-tight">
            {t("auth.login_title")}
          </h1>
          <p className="mt-1.5 text-[13.5px] text-zinc-500">
            {t("auth.login_subtitle")}
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-3.5">
            <label className="block">
              <span className="mb-1.5 block text-[12.5px] font-medium text-zinc-600 dark:text-zinc-400">
                {t("auth.email")}
              </span>
              <input
                type="email"
                autoComplete="email"
                required
                data-testid="portal-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="te@cegnev.hu"
                className="block h-12 w-full rounded-xl bg-zinc-100 px-3.5 text-[16px] text-zinc-900 placeholder:text-zinc-400 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-800/60 dark:text-white dark:focus:bg-zinc-800 dark:focus:ring-blue-500"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[12.5px] font-medium text-zinc-600 dark:text-zinc-400">
                {t("auth.password")}
              </span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  data-testid="portal-login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="block h-12 w-full rounded-xl bg-zinc-100 px-3.5 pr-12 text-[16px] text-zinc-900 placeholder:text-zinc-300 transition-colors focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-800/60 dark:text-white dark:focus:bg-zinc-800 dark:focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-1.5 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-zinc-400 transition hover:bg-zinc-200/60 hover:text-zinc-900 dark:hover:bg-zinc-700/60 dark:hover:text-white"
                  aria-label={t("auth.password")}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            <button
              type="submit"
              data-testid="portal-login-submit"
              disabled={submitting || !email || !password}
              className="group mt-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-500 text-[14.5px] font-semibold tracking-tight text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition-all hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:shadow-none dark:disabled:bg-zinc-700"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {t("auth.sign_in")}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[12.5px] text-zinc-500">
              Probléma a belépéssel?{" "}
              <span className="font-medium text-zinc-700 dark:text-zinc-300">
                Fordulj a HR-hez
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-[11.5px] text-zinc-400">
          © {new Date().getFullYear()} SIPX Group · /dolgozo
        </p>
      </div>
    </div>
  );
}
